/**
 * Configurações → Usuários e Permissões
 * Master pode: criar, editar, ativar/desativar, redefinir senha, vincular unidade, definir função.
 * Roles apresentadas: super_master (o master atual), unit_admin, employee.
 * Enforcement de isolamento por unidade permanece via `unit_ids[]`.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound, ShieldCheck, Users, Settings2 } from "lucide-react";
import { PermissionsPanel } from "@/pages/PermissionsPanel";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Frontend usa APENAS as roles que realmente existem no backend hoje:
//   - master (o único usuário master do sistema)
//   - user   (todos os demais — vinculados a uma unidade via unit_ids[])
// Rótulos futuros (super_master / unit_admin / employee) não são exibidos
// enquanto não houver enforcement real no backend.
function displayRole(u) {
  if (u.role === "master") {
    return { key: "master", label: "Master", cls: "bg-purple-100 text-purple-800 border-purple-200" };
  }
  return { key: "user", label: "Usuário", cls: "bg-slate-100 text-slate-700 border-slate-200" };
}

export default function UsersPermissions() {
  const { apiCall, user: current } = useAuth();
  const isMaster = current?.role === "master";

  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(null);   // user obj

  // iter75 — Painel de permissões
  const [permPanelUser, setPermPanelUser] = useState(null);
  const [permPanelOpen, setPermPanelOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [switchTargetPending, setSwitchTargetPending] = useState(null); // usuário para trocar quando dirty
  const [panelDirty, setPanelDirty] = useState(false); // rastreado via callback

  // form state — cria usuário comum (role=user).
  // Toda criação exige unit_id obrigatório para garantir isolamento por unidade.
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    unit_id: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [uu, un] = await Promise.all([
        apiCall("get", "/team/members"),
        apiCall("get", "/units"),
      ]);
      setUsers(uu.data || []);
      setUnits(un.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  const loadCatalog = async () => {
    try {
      const r = await apiCall("get", "/permissions/catalog");
      setCatalog(r.data);
    } catch (e) {
      // catalog é essencial — bloqueia painel se falhar
      setCatalog(null);
      toast.error("Falha ao carregar catálogo de permissões");
    }
  };

  useEffect(() => {
    load();
    loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPermissions = (u) => {
    if (permPanelOpen && panelDirty && permPanelUser?.user_id !== u.user_id) {
      // troca com pendências
      setSwitchTargetPending(u);
      return;
    }
    setPermPanelUser(u);
    setPermPanelOpen(true);
  };

  const confirmSwitchUser = () => {
    const u = switchTargetPending;
    setSwitchTargetPending(null);
    setPanelDirty(false);
    setPermPanelUser(u);
    setPermPanelOpen(true);
  };

  if (!isMaster) {
    return (
      <div className="p-6 max-w-2xl mx-auto text-center text-sm text-muted-foreground">
        Você não tem permissão para gerenciar usuários. Fale com o Super Master.
      </div>
    );
  }

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Preencha nome, e-mail e senha temporária");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Senha deve ter pelo menos 6 caracteres");
      return;
    }
    if (!form.unit_id) {
      toast.error("Selecione uma unidade — todo usuário precisa estar vinculado a uma");
      return;
    }
    try {
      await apiCall("post", "/team/members", {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        team_role: "recepcao",
        unit_ids: [form.unit_id],
      });
      toast.success("Usuário criado com sucesso");
      setCreateOpen(false);
      setForm({ name: "", email: "", password: "", unit_id: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar");
    }
  };

  const handleDelete = async (u) => {
    if (!confirm(`Remover o acesso de ${u.name}? Isso não apaga histórico, apenas revoga o login.`)) return;
    try {
      await apiCall("delete", `/team/members/${u.user_id}`);
      toast.success("Acesso removido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover");
    }
  };

  const handlePasswordReset = async () => {
    const u = pwOpen;
    const np = (document.getElementById("pw-reset-input") || {}).value || "";
    if (np.length < 6) {
      toast.error("Senha deve ter pelo menos 6 caracteres");
      return;
    }
    try {
      await apiCall("post", `/team/members/${u.user_id}/reset-password`, { new_password: np });
      toast.success(`Senha de ${u.name} redefinida`);
      setPwOpen(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao resetar");
    }
  };

  const handleChangeUnit = async (u, unit_id) => {
    try {
      await apiCall("patch", `/team/members/${u.user_id}/units`, {
        unit_ids: unit_id ? [unit_id] : [],
      });
      toast.success("Unidade atualizada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao atualizar");
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="users-permissions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Users size={22} /> Usuários e Permissões
          </h1>
          <p className="text-sm text-muted-foreground">
            Gerencie logins da equipe, atribua unidade e defina o papel de cada um.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-user-btn">
          <UserPlus size={16} className="mr-2" /> Novo usuário
        </Button>
      </div>

      {/* Legend — apenas roles que existem hoje */}
      <div className="text-xs bg-slate-50 border rounded p-3 space-y-1">
        <div className="font-semibold">Funções atuais:</div>
        <div><b>Master</b>: dono do sistema. Acesso total. Não pode ser criado por esta tela.</div>
        <div><b>Usuário</b>: acesso à unidade em que estiver vinculado. O isolamento por unidade é aplicado nas telas via <code>unit_ids[]</code>.</div>
        <div className="text-[11px] text-muted-foreground pt-1 border-t mt-2">
          Estrutura de permissões refinada (administrador por unidade + funcionário) será implementada em iteração dedicada, com enforcement real no backend.
        </div>
      </div>

      {loading ? (
        <Card className="p-6 text-center text-muted-foreground">Carregando…</Card>
      ) : (
        <Card>
          <table className="w-full text-sm">
            <thead className="border-b bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">Nome</th>
                <th className="text-left px-3 py-2">E-mail</th>
                <th className="text-left px-3 py-2">Função</th>
                <th className="text-left px-3 py-2">Unidade</th>
                <th className="text-right px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody data-testid="users-table-body">
              {users.map((u) => {
                const dr = displayRole(u);
                const isSelf = u.user_id === current.user_id;
                return (
                  <tr key={u.user_id} className="border-b" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-3 py-2">{u.name}{isSelf && <span className="text-xs text-muted-foreground ml-1">(você)</span>}</td>
                    <td className="px-3 py-2">{u.email}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded border ${dr.cls}`}>{dr.label}</span>
                    </td>
                    <td className="px-3 py-2">
                      {u.role === "master" ? (
                        <span className="text-xs text-muted-foreground">Todas as unidades</span>
                      ) : (
                        <Select
                          value={(u.unit_ids || [])[0] || ""}
                          onValueChange={(v) => handleChangeUnit(u, v)}
                        >
                          <SelectTrigger className="w-56 h-8 text-xs" data-testid={`unit-select-${u.user_id}`}>
                            <SelectValue placeholder="— nenhuma —" />
                          </SelectTrigger>
                          <SelectContent>
                            {units.map((un) => (
                              <SelectItem key={un.unit_id} value={un.unit_id}>
                                {un.name} {un.active === false ? "(inativa)" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openPermissions(u)}
                        data-testid={`perm-btn-${u.user_id}`}
                        title="Permissões"
                      >
                        <Settings2 size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPwOpen(u)}
                        data-testid={`pw-reset-btn-${u.user_id}`}
                        title="Redefinir senha"
                      >
                        <KeyRound size={14} />
                      </Button>
                      {!isSelf && u.role !== "master" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                          onClick={() => handleDelete(u)}
                          data-testid={`del-btn-${u.user_id}`}
                          title="Remover acesso"
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Create user dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Novo usuário</DialogTitle>
            <DialogDescription>Crie um login para a equipe. A senha temporária poderá ser trocada pelo próprio usuário depois.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="user-form-name" />
            </div>
            <div>
              <Label>E-mail</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="user-form-email" />
            </div>
            <div>
              <Label>Senha temporária</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="user-form-password" />
            </div>
            <div>
              <Label>Unidade *</Label>
              <Select value={form.unit_id} onValueChange={(v) => setForm({...form, unit_id: v})}>
                <SelectTrigger data-testid="user-form-unit">
                  <SelectValue placeholder="Selecione…" />
                </SelectTrigger>
                <SelectContent>
                  {units.filter(u => u.active !== false).map((un) => (
                    <SelectItem key={un.unit_id} value={un.unit_id}>{un.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-muted-foreground mt-1">
                Vínculo obrigatório. O isolamento por unidade é enforceado nas telas.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} data-testid="user-form-submit">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password reset dialog */}
      <Dialog open={!!pwOpen} onOpenChange={(o) => !o && setPwOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={18} /> Redefinir senha
            </DialogTitle>
            <DialogDescription>
              Nova senha para <b>{pwOpen?.name}</b>. Ao concluir, comunique fora deste sistema.
            </DialogDescription>
          </DialogHeader>
          <Input id="pw-reset-input" type="text" placeholder="Nova senha (min 6 chars)" data-testid="pw-reset-input" />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPwOpen(null)}>Cancelar</Button>
            <Button onClick={handlePasswordReset} data-testid="pw-reset-submit">Redefinir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter75 — Painel de permissões */}
      {permPanelUser && catalog && (
        <PermissionsPanel
          open={permPanelOpen}
          onOpenChange={(o) => { setPermPanelOpen(o); if (!o) setPanelDirty(false); }}
          targetUser={permPanelUser}
          catalog={catalog}
          users={users.filter(u => u.role !== "master")}
          onDirtyChange={setPanelDirty}
          onSwitchUser={(uid) => {
            const u = users.find(x => x.user_id === uid);
            if (u) setPermPanelUser(u);
            setPanelDirty(false);
          }}
          onSaved={() => { setPanelDirty(false); load(); }}
        />
      )}

      {/* Confirmar troca de usuário com pendências (via cliques externos na tabela) */}
      <AlertDialog open={!!switchTargetPending} onOpenChange={(o) => !o && setSwitchTargetPending(null)}>
        <AlertDialogContent data-testid="permissions-switch-user-dialog-outer">
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar alterações?</AlertDialogTitle>
            <AlertDialogDescription>
              Há alterações não salvas em <b>{permPanelUser?.name}</b>. Trocar para <b>{switchTargetPending?.name}</b> vai descartá-las.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="permissions-switch-cancel-outer">Manter editando</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSwitchUser} data-testid="permissions-switch-confirm-outer">
              Descartar e trocar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
